import { HttpClient, HttpContext, HttpEvent, HttpParams } from '@angular/common/http';
import { SHOW_LOADER } from '../core/interceptors/loader.interceptor';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ICreditLineCommentsRequest,
  ICreditLines,
  IFinancingOptions,
  IPublicDocuments,
  IFinancingRequest,
  IFinancingRequestBack,
  IPostCreditLines,
  ICreditLineStatus,
} from '../models/financing-request.model';
import {
  INosisListadoActividades,
  IUserNosisDetails,
} from '../models/user-nosis-details.model';
import { map } from 'rxjs/operators';
import { ICreditLinesFilters } from '../models/credit-lines-filters.model';
import { and, comparison, eq, ge, inList, le } from 'rsql-builder';
import { PageableApiResult } from '../models/pagable-api-result.model';
import { PageableModel } from '../models/pageable.model';
import { ICreditOffersFilters } from '../models/offers-filters.model';
import { DateTime } from 'luxon';
import { ICompanyActivity, IPublicProfile } from '../models/public-profile.model';
import { ITrackRecord } from '../models/financing-track-record.model';
import { ICreditOffers } from '../models/financing-offers.model';
import { IBalanceClosingDateRequest, ICurrentBalance } from '../models/financing-negotiations.model';

@Injectable({
  providedIn: 'root',
})
export class FinancingApiService {
  constructor(private http: HttpClient) {}
  
  public getFinancialOptions(): Observable<IFinancingOptions> {
    return this.http.get<IFinancingOptions>(`${environment.apiBaseUrl}/core/financial-operation/get-financial-options`);
  }

  public getCreditRequestStatusOptions(): Observable<ICreditLineStatus[]> {
    return this.http.get<ICreditLineStatus[]>(`${environment.apiBaseUrl}/financiamientos/portal/get-statuses`)
  }

  public getInstrumentOptions(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financiamientos/instrumentaciones/tipos`)
  }
  
  public getCompanyActivities(): Observable<ICompanyActivity[]> {
    return this.http.get<ICompanyActivity[]>(`${environment.apiBaseUrl}/core/public/get-actividades-empresas`)
  }

  /**
   * Balance vigente de la empresa (`currentFile`) + histórico de ventas declaradas
   * (`declaracionesVentas`, usado para la precarga del bloque de ventas — CA21).
   */
  public getCurrentBalance(): Observable<ICurrentBalance> {
    return this.http.get<ICurrentBalance>(`${environment.apiBaseUrl}/core/portal/profile/documents/current-balance`);
  }

  /**
   * CA32: balance vigente y ventas declaradas de una PyME, para que el inversor los vea en el
   * detalle de la solicitud. `userId` es el id de la PyME que pidió el financiamiento.
   */
  public getCurrentBalanceByUserId(userId: number): Observable<ICurrentBalance> {
    return this.http.get<ICurrentBalance>(`${environment.apiBaseUrl}/core/portal/profile/documents/current-balance/${userId}`);
  }

  /**
   * CA10: guarda la fecha de cierre corregida por el usuario.
   * `id` es el id del análisis que devuelve `/core/portal/jobs/{jobId}/status`; `closingDate` 'YYYY-MM-DD'.
   * Sin loader global: se guarda en segundo plano mientras el usuario sigue completando el form.
   */
  public updateBalanceClosingDate(body: IBalanceClosingDateRequest): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/core/portal/profile/documents/current-balance/closing-date`, body, {
      context: new HttpContext().set(SHOW_LOADER, false)
    });
  }

  /**
   * Sube el balance y dispara el análisis por IA (asincrónico). Responde `{ jobId }`.
   * Emite eventos HTTP para poder mostrar la barra de progreso de la carga (CA7).
   * Body form-data: file, razonSocial, cuit, docTypeHint=BALANCE, userId.
   */
  public uploadNewBalance(body: FormData): Observable<HttpEvent<any>> {
    return this.http.post(`${environment.apiBaseUrl}/core/portal/profile/documents/analysis/async`, body, {
      reportProgress: true,
      observe: 'events',
      // La pantalla muestra su propio progreso; no bloquear con el loader global.
      context: new HttpContext().set(SHOW_LOADER, false)
    });
  }

  public getPublicProfile(): Observable<IPublicProfile> {
    return this.http.get<IPublicProfile>(`${environment.apiBaseUrl}/core/portal/public_profile/get_one`)
  }
  
  public saveNewPublicProfile(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/core/portal/public_profile`, body)
  }
  
  public editPublicProfile(body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/core/portal/public_profile`, body)
  }
  
  public checkFinancingDocuments(): Observable<any> {
    return this.http.get<any>(`${environment.apiBaseUrl}/core/financial-operation/check-financial-documents`, { observe: 'response' });
  }
  
  public sendFinancingRequest(body: IPostCreditLines): Observable<any> {
    return this.http.post<any>(
      `${environment.apiBaseUrl}/financiamientos/portal/save-user-financial-request`,
      body
    );
  }
  
  public getFinancingRequestById(id: number | string): Observable<ICreditLines> {
    return this.http.get<ICreditLines>(`${environment.apiBaseUrl}/financiamientos/portal/${id}`)
  }

  public editFinancingRequest(body: IPostCreditLines, id: number): Observable<any> {
    return this.http.put<any>(
      `${environment.apiBaseUrl}/financiamientos/portal/update-financiamiento/${id}`,
      body
    );
  }

  public cancelFinancingRequest(body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/portal/cancel-financial-request`, body)
  }
  
  public extendFinancingRequest(body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/portal/request-extend`, body)
  }

  public getCreditLinesList(filter?: ICreditLinesFilters): Observable<PageableApiResult<ICreditLines>> {
    const comps = [];
    let nofilter = true;

    if (filter.montoMin) {
      comps.push(comparison('montoRequerido', ge(filter.montoMin)));
      nofilter = false;
    }
    if (filter.montoMax) {
      comps.push(comparison('montoRequerido', le(filter.montoMax)));
      nofilter = false;
    }
    if (filter.score) {
      comps.push(comparison('score', eq(filter.score)));
      nofilter = false;
    }
    if (filter.razonSocial) {
      const razonSocialValue = filter.razonSocial.replace(/\s/g, '%');
      comps.push('razonSocial==' + '*' + razonSocialValue + '*');
      nofilter = false;
    }
    if (filter.idNumber && filter.idNumber.length >= 4) {
      comps.push(comparison('idNumber', eq(filter.idNumber + '*')));
      nofilter = false;
    }
    if (filter.facturacionMin) {
      comps.push(comparison('facturacionEstimada', ge(filter.facturacionMin)));
      nofilter = false;
    }
    if (filter.facturacionMax) {
      comps.push(comparison('facturacionEstimada', le(filter.facturacionMax)));
      nofilter = false;
    }
    if (filter.fechaMin) {
      const fecha = filter.fechaMin instanceof DateTime ? filter.fechaMin.startOf('day').toJSDate().toISOString() : filter.fechaMin?.setUTCHours(0,0,0,0);
      comps.push(comparison('createdDate', ge(fecha)));
      nofilter = false;
    }
    if (filter.fechaMax) {
      const fecha = filter.fechaMax instanceof DateTime ? filter.fechaMax.endOf('day').toJSDate().toISOString() : filter.fechaMax?.setUTCHours(23,59,59,99);
      comps.push(comparison('createdDate', le(fecha)));
      nofilter = false;
    }
    if (filter.daGarantia != null) {
      comps.push(comparison('requiereGarantia', eq(filter.daGarantia)));
      nofilter = false;
    }
    if (filter.userType) {
      comps.push(comparison('userType', eq(filter.userType)));
      nofilter = false;
    }
    if (filter.favoritos != null) {
      comps.push(comparison('empresaInteresada', eq(filter.favoritos)));
      nofilter = false;
    }
    if (filter.tipoGarantia) {
      comps.push(comparison('garantiaId', eq(filter.tipoGarantia)));
      nofilter = false;
    }
    if (filter.tipoInstrumento && filter?.tipoInstrumento?.length > 0) {
      comps.push(comparison('tipoInstrumentacion', inList(...filter.tipoInstrumento)));
      nofilter = false;
    }
    if (filter.rubro && filter.rubro.length > 0) {
      const rubroIds = filter.rubro.filter(id => id !== 0);
      if (rubroIds.length > 0) {
        comps.push(`actividadEmpresaId=in=(${rubroIds.join(',')})`);
        nofilter = false;
      }
    }

    if (nofilter) {
      comps.push(comparison('montoRequerido', ge(0)));
    }

    let params: HttpParams = null;
    if (comps.length > 0) {
      let q = and(...comps);
      params = new HttpParams().set('rquery', q);
    }

    if (filter.sortByMonto) {
      params = params.append('rsort', 'montoRequerido').append('rdirection', filter.sortByMonto);
    }
    if (filter.sortByRazonSocial) {
      params = params.append('rsort', 'razonSocial').append('rdirection', filter.sortByRazonSocial);
    }
    if (filter.sortByFacturacion) {
      params = params.append('rsort', 'facturacionEstimada').append('rdirection', filter.sortByFacturacion);
    }
    if (filter.sortByScore) {
      params = params.append('rsort', 'score').append('rdirection', filter.sortByScore);
    }
    if (filter.sortByGarantia) {
      params = params.append('rsort', 'requiereGarantia').append('rdirection', filter.sortByGarantia);
    }
    if (filter.sortByFecha) {
      params = params.append('rsort', 'fecha').append('rdirection', filter.sortByFecha);
    }

    if (filter.page) {
      params = params.append('page', filter.page.toString());
    }
    if (filter.size) {
      params = params.append('size', filter.size.toString());
    }

    return this.http.get<PageableApiResult<ICreditLines>>(
      `${environment.apiBaseUrl}/financiamientos/portal/get-all-companies-seeking-funding-list`, {params}
    );
  }

  public getPublicProfileById(id: string | number): Observable<IFinancingRequest> {
    return this.http
      .get<IFinancingRequestBack>(
        `${
          environment.apiBaseUrl
        }/core/financial-operation/get-user-public-profile-data${id ? `/${id}` : ''}`
      )
      .pipe(
        map((response) => {
          return {
            id: response.id,
            userId: response.userId,
            montoRequerido: response.montoRequerido,
            motivoDeFinanciamiento: response.motivoDeFinanciamiento,
            garantia: response.garantia,
            requiereGarantia: response.requiereGarantia,
            idNumber: response.idNumber,
            nosisData: this.mapNosisDetailsFromJSON(response.nosisData) || null,
            empresaInteresada: response.empresaInteresada,
            sitioWeb: response.sitioWeb,
            descripcionEmpresa: response.descripcionEmpresa,
            status: response.status,
            motivoRechazo: response.motivoRechazo,
            actividad: response.actividad,
            cuit: response.cuit,
            otraActividad: response.otraActividad,
            presentacion: response.presentacion,
            razonSocial: response.razonSocial
          };
        })
      );
  }

  public getTrackRecord(userId: number): Observable<ITrackRecord> {
    return this.http.get<ITrackRecord>(`${environment.apiBaseUrl}/financiamientos/portal/get-track-record-pyme/${userId}`);
  }

  public getSelfTrackRecord(): Observable<ITrackRecord> {
    return this.http.get<ITrackRecord>(`${environment.apiBaseUrl}/financiamientos/portal/get-track-record`);
  }

  public sendCreditLineInterest(body: any): Observable<any> {
    return this.http.post<any>(`${environment.apiBaseUrl}/core/financial-operation/save-pending-funding-operation`, body);
  }

  // public getOfferById(id: any): Observable<ICreditOffers> {
  //   return this.http.get<ICreditOffers>(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-offer/${id}`);
  // }

  public updateCreditLineStatus(body: any): Observable<any> {
    return this.http.put<any>(
      `${environment.apiBaseUrl}/core/financial-operation/update-self-financial-requirement-status`, body
    );
  }

  public reactivateCreditLine(id: any): Observable<any> {
    return this.http.put<any>(`${environment.apiBaseUrl}/core/financial-operation/return-previous-request-status/${id}`, {});
  }

  public sendCreditLineComment(body: ICreditLineCommentsRequest): Observable<any> {
    return this.http.post<any>(
      `${environment.apiBaseUrl}/core/financial-operation/save-financial-extra-requirement`, body
    );
  }

  public getSelfCreditLinesRequests(filter: any): Observable<PageableModel<ICreditLines>> {
    const comps = []
    let noFilter = true;

    
    if (filter.montoMin) {
      comps.push(comparison('montoRequerido', ge(filter.montoMin)));
      noFilter = false;
    }
    if (filter.montoMax) {
      comps.push(comparison('montoRequerido', le(filter.montoMax)));
      noFilter = false;
    }
    if (filter.motivo) {
      const motivoValue = filter.motivo.replace(/\s/g, '%');
      comps.push(`motivoDeFinanciamiento==*${motivoValue}*`);
      noFilter = false;
    }
    if (filter.estado) {
      comps.push(comparison('status', eq(filter.estado)));
      noFilter = false;
    }
    if (filter.fechaMin) {
      const fecha = filter.fechaMin instanceof DateTime ? filter.fechaMin.startOf('day').toJSDate().toISOString() : filter.fechaMin?.setUTCHours(0,0,0,0);
      comps.push(comparison('createdDate', ge(fecha)));
      noFilter = false;
    }
    if (filter.fechaMax) {
      const fecha = filter.fechaMax instanceof DateTime ? filter.fechaMax.endOf('day').toJSDate().toISOString() : filter.fechaMax?.setUTCHours(23,59,59,99);
      comps.push(comparison('createdDate', le(fecha)));
      noFilter = false;
    }
    if (filter.daGarantia != null) {
      comps.push(comparison('requiereGarantia', eq(filter.daGarantia)));
      noFilter = false;
    }
    if (filter.tipoGarantia) {
      comps.push(comparison('garantiaId', eq(filter.tipoGarantia)));
      noFilter = false;
    }
    if (filter.rubro && filter.rubro.length > 0) {
      const rubroIds = filter.rubro.filter(id => id !== 0);
      if (rubroIds.length > 0) {
        comps.push(`actividadEmpresaId=in=(${rubroIds.join(',')})`);
        noFilter = false;
      }
    }

    if (filter.tipoInstrumento && filter.tipoInstrumento.length > 0) {
      comps.push(comparison('tipoInstrumentacion', inList(...filter.tipoInstrumento)));
      noFilter = false;
    }

    if (noFilter) {
      comps.push(comparison('montoRequerido', ge(0)));
    }

    let params: HttpParams = null;
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = new HttpParams().set('rquery', q);
    }

    if (filter.sortByMonto) {
      params = params
        .append('rsort', 'montoRequerido')
        .append('rdirection', filter.sortByMonto);
    }
    if (filter.sortByRazonSocial) {
      params = params
        .append('rsort', 'razonSocial')
        .append('rdirection', filter.sortByRazonSocial);
    }
    if (filter.sortByScore) {
      params = params
        .append('rsort', 'score')
        .append('rdirection', filter.sortByScore);
    }
    if (filter.sortByGarantia) {
      params = params
        .append('rsort', 'requiereGarantia')
        .append('rdirection', filter.sortByGarantia);
    }
    if (filter.sortByIntencion) {
      params = params
        .append('rsort', 'intencionCredito')
        .append('rdirection', filter.sortByIntencion);
    }
    if (filter.sortByStatus) {
      params = params
        .append('rsort', 'status')
        .append('rdirection', filter.sortByStatus);
    }
    if (filter.sortByMotivo) {
      params = params
        .append('rsort', 'motivoDeFinanciamiento')
        .append('rdirection', filter.sortByMotivo);
    }
    if (filter.sortByFecha) {
      params = params
        .append('rsort', 'createdDate')
        .append('rdirection', filter.sortByFecha);
    }

    if (filter.page) {
      params = params.append('page', filter.page.toString());
    }
    if (filter.size) {
      params = params.append('size', filter.size.toString());
    }
    return this.http.get<any>(
      `${environment.apiBaseUrl}/core/financial-operation/get-self-financial-requests`, {params}
    );
  }

  public getPublicProfileDocumentsById(id: number): Observable<IPublicDocuments[]> {
    return this.http.get<IPublicDocuments[]>(
      `${environment.apiBaseUrl}/core/financial-operation/get-user-public-profile-documents/${id}`
    );
  }

  public getDocumentsByRequestId(id: number): Observable<any> {
    return this.http.get<any>(
      `${environment.apiBaseUrl}/financiamientos/portal/get-docs-solicitud/${id}`
    );
  }

  private mapNosisDetailsFromJSON(data: string): IUserNosisDetails {
    if (!data) return null;
    const jsonData = JSON.parse(data);

    const nosisDetails: IUserNosisDetails = {
      fechaActualizacion: jsonData.fechaActualizacion,
      razonSocial: jsonData.razonSocial,
      apellido: jsonData.apellido,
      nombre: jsonData.nombre,
      DNI: jsonData.DNI,
      idNumber: jsonData.idNumber,
      antiguedadAfip: jsonData.antiguedadAfip,
      actividadPrincipal: {
        descripcion: jsonData.actividadPrincipal.descripcion,
        sector: jsonData.actividadPrincipal.sector,
      },
      peorSituacion: jsonData.peorSituacion,
      cantidadBancos: jsonData.cantidadBancos,
      cumplimientoDeudor: jsonData.cumplimientoDeudor,
      cantidadChequesRechazados: jsonData.cantidadChequesRechazados,
      cantidadChequesSinFondos: jsonData.cantidadChequesSinFondos,
      montoChequesRechazados: jsonData.montoChequesRechazados,
      score: jsonData.score,
      nivelSocioEconomico: jsonData.nivelSocioEconomico,
      facturacionEstimada: jsonData.facturacionEstimada,
      edadEmpresa: jsonData.edadEmpresa,
      esMonotributo: jsonData.esMonotributo,
      esAutonomo: jsonData.esAutonomo,
      esSujetoObligado: jsonData.esSujetoObligado,
      esPersonaPolitica: jsonData.esPersonaPolitica,
      actividadesListado: this.mapActividadesListadoFromJSON(
        jsonData?.actividadesListado
      ),
      source: jsonData.source,
    };

    return nosisDetails;
  }

  public mapActividadesListadoFromJSON(
    data: any[]
  ): INosisListadoActividades[] {
    return data.map((actividad) => ({
      codigoActividad: actividad.codigoActividad,
      descripcion: actividad.descripcion,
      fechaInicio: new Date(actividad.fechaInicio),
      tipo: actividad.tipo,
      sector: actividad.sector,
      clanae3: actividad.clanae3,
      clanae5: actividad.clanae5,
      categoria: actividad.categoria,
    }));
  }

  public uploadRequestDocuments(id: number, body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/portal/upload-docs-solicitud/${id}`, body)
  }
}
